import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Phone,
  Play,
  Clock,
  Calendar,
  ExternalLink,
  Copy,
  Check,
  Award,
  FileText,
} from "lucide-react";
import apiService from "../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";

// ─── Utility ────────────────────────────────────────────────────────────────
const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
};

const formatPhone = (phone) => {
  if (!phone) return "—";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1"))
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone;
};

const formatDate = (date, opts = {}) => {
  if (!date) return "—";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...opts,
  });
};

// ─── Sub-components ─────────────────────────────────────────────────────────
const MetaRow = ({ icon: Icon, label, children, isDarkMode }) => (
  <div
    className={`flex items-start gap-3 py-3 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"} last:border-0`}
  >
    <div
      className={`w-8 h-8 rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-gray-100"} flex items-center justify-center shrink-0 mt-0.5`}
    >
      <Icon
        size={16}
        className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      />
    </div>
    <div className="flex-1 min-w-0">
      <div
        className={`${isDarkMode ? "text-gray-500" : "text-gray-400"} text-xs font-medium uppercase tracking-wider mb-0.5`}
      >
        {label}
      </div>
      <div
        className={`${isDarkMode ? "text-gray-200" : "text-gray-700"} text-sm`}
      >
        {children}
      </div>
    </div>
  </div>
);

const CopyButton = ({ text, isDarkMode }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className={`${isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"} transition-colors p-1 ml-1`}
      title="Copy"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
};

const QualBadge = ({ record, isDarkMode }) => {
  if (record.isQualified === true) {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full border border-green-800/60 text-xs font-medium text-green-400 ${isDarkMode ? "bg-green-900/20" : "bg-green-50"}`}
      >
        <div className="w-2 h-2 rounded-full bg-green-400 mr-2" />
        Qualified
      </span>
    );
  }
  if (record.isQualified === false) {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full border border-red-800/60 text-xs font-medium text-red-400 ${isDarkMode ? "bg-red-900/20" : "bg-red-50"}`}
      >
        <div className="w-2 h-2 rounded-full bg-red-400 mr-2" />
        Unqualified
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full border border-[#373A41] text-xs font-medium text-gray-400 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`}
    >
      <div className="w-2 h-2 rounded-full bg-gray-500 mr-2" />
      Pending
    </span>
  );
};

// ─── Page ───────────────────────────────────────────────────────────────────
const LeadCallDataDetail = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRawData, setShowRawData] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const response = await apiService.client.get(
          `/lead-call-data/${callId}`,
        );
        setRecord(response.data);
      } catch (error) {
        console.error("Error fetching call record:", error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [callId]);

  // ─── Loading ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          Loading call details…
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex flex-col items-center justify-center gap-4`}
      >
        <div
          className={`text-lg ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          Call record not found
        </div>
        <button
          onClick={() => navigate("/lead-call-data")}
          className={`px-4 py-2 ${isDarkMode ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"} rounded-lg transition-colors text-sm`}
        >
          Back to Call Data
        </button>
      </div>
    );
  }

  const lead = record.lead || {};
  const leadInitials = (lead.fullName || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  let endTime = null;
  if (record.callStartTime && record.duration) {
    endTime = new Date(
      new Date(record.callStartTime).getTime() + record.duration * 1000,
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Back nav */}
        <button
          onClick={() => navigate("/lead-call-data")}
          className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors mb-6 text-sm`}
        >
          <ArrowLeft size={16} />
          Back to Call Data
        </button>

        {/* Hero card */}
        <div
          className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} rounded-xl border p-6 mb-6`}
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div
                className={`w-14 h-14 rounded-full ${isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-300 text-gray-600"} flex items-center justify-center text-lg font-semibold shrink-0`}
              >
                {leadInitials}
              </div>

              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1
                    className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {lead.fullName || "Unknown Lead"}
                  </h1>
                  <QualBadge record={record} isDarkMode={isDarkMode} />
                </div>
                <div
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {lead.email || "—"}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <Phone size={13} className="text-blue-400" />
                  <a
                    href={`tel:${record.phoneNumber}`}
                    className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    {formatPhone(record.phoneNumber)}
                  </a>
                  <CopyButton
                    text={record.phoneNumber}
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>
            </div>

            {/* Link to lead */}
            <button
              onClick={() => navigate(`/leads/${record.leadId}`)}
              className={`flex items-center gap-2 px-3 py-1.5 ${isDarkMode ? "bg-[#141820] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"} border rounded-lg transition-colors text-sm shrink-0`}
            >
              <ExternalLink size={14} />
              Open Lead
            </button>
          </div>
        </div>

        {/* Two-column body */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left — metadata */}
          <div className="lg:col-span-2">
            <div
              className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} rounded-xl border p-6`}
            >
              <h2
                className={`text-sm font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider mb-4`}
              >
                Call Details
              </h2>

              <MetaRow icon={Play} label="Duration" isDarkMode={isDarkMode}>
                {formatDuration(record.duration)}
              </MetaRow>

              <MetaRow icon={Calendar} label="Started" isDarkMode={isDarkMode}>
                {formatDate(record.callStartTime)}
              </MetaRow>

              {endTime && (
                <MetaRow icon={Clock} label="Ended" isDarkMode={isDarkMode}>
                  {formatDate(endTime)}
                </MetaRow>
              )}

              <MetaRow
                icon={Award}
                label="Qualification Score"
                isDarkMode={isDarkMode}
              >
                {record.qualificationScore != null ? (
                  <div className="flex items-center gap-2">
                    <span>{record.qualificationScore}</span>
                    <div
                      className={`flex-1 h-1.5 ${isDarkMode ? "bg-gray-800" : "bg-gray-200"} rounded-full overflow-hidden max-w-[100px]`}
                    >
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(record.qualificationScore, 100)}%`,
                          background:
                            record.qualificationScore >= 70
                              ? "#4ade80"
                              : record.qualificationScore >= 40
                                ? "#facc15"
                                : "#f87171",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    —
                  </span>
                )}
              </MetaRow>

              <MetaRow
                icon={Phone}
                label="Call System ID"
                isDarkMode={isDarkMode}
              >
                <div className="flex items-center flex-wrap">
                  <span
                    className={`font-mono text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} break-all`}
                  >
                    {record.callSystemCallId}
                  </span>
                  <CopyButton
                    text={record.callSystemCallId}
                    isDarkMode={isDarkMode}
                  />
                </div>
              </MetaRow>

              {record.appointmentId && (
                <MetaRow
                  icon={Calendar}
                  label="Linked Appointment"
                  isDarkMode={isDarkMode}
                >
                  <span
                    className={`font-mono text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {record.appointmentId}
                  </span>
                </MetaRow>
              )}

              {record.recordingUrl && (
                <MetaRow icon={Play} label="Recording" isDarkMode={isDarkMode}>
                  <a
                    href={record.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
                  >
                    Open Recording <ExternalLink size={12} />
                  </a>
                </MetaRow>
              )}
            </div>
          </div>

          {/* Right — summary + raw JSON */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            {/* Summary card */}
            <div
              className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} rounded-xl border p-6 flex-1`}
            >
              <div className="flex items-center gap-2 mb-4">
                <FileText
                  size={16}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
                <h2
                  className={`text-sm font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
                >
                  Call Summary
                </h2>
              </div>

              {record.summary ? (
                <p
                  className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  {record.summary}
                </p>
              ) : (
                <p
                  className={`text-sm italic ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                >
                  No summary available for this call.
                </p>
              )}
            </div>

            {/* Raw call data toggle */}
            <div
              className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} rounded-xl border overflow-hidden`}
            >
              <button
                onClick={() => setShowRawData((prev) => !prev)}
                className={`w-full flex items-center justify-between px-6 py-4 ${isDarkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-100/80"} transition-colors`}
              >
                <div className="flex items-center gap-2">
                  <FileText
                    size={16}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                  <span
                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
                  >
                    Raw Call Data
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {record.callData && (
                    <CopyButton
                      text={JSON.stringify(record.callData, null, 2)}
                      isDarkMode={isDarkMode}
                    />
                  )}
                  <span
                    className={`${isDarkMode ? "text-gray-500" : "text-gray-400"} text-xs transition-transform inline-block ${showRawData ? "rotate-180" : ""}`}
                  >
                    ▼
                  </span>
                </div>
              </button>

              {showRawData && (
                <div
                  className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                >
                  <pre
                    className={`p-4 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} overflow-auto max-h-96 ${isDarkMode ? "bg-[#0a0c10]" : "bg-gray-100"} font-mono leading-relaxed`}
                  >
                    {record.callData
                      ? JSON.stringify(record.callData, null, 2)
                      : "No raw data available."}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadCallDataDetail;
